/**
 * Outlook Inbox — connects one or more Microsoft 365 mailboxes so inbound email
 * becomes Velaro tickets or conversations. Uses the dedicated OutlookOAuth flow
 * (separate from the Microsoft 365 calendar/OneDrive connection).
 *
 * OAuth flow:
 *   1. GET OutlookOAuth/auth-url → { authUrl }  (authenticated via Bearer token)
 *   2. window.location.href = authUrl            (browser goes to Microsoft sign-in)
 *   3. Microsoft → OutlookOAuth/callback → redirect back with ?outlookConnected=true
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";

interface OutlookMailbox {
  id: number;
  mailboxEmail: string;
  label: string | null;
  teamId: number | null;
  teamName: string | null;
  isEnabled: boolean;
  connectedAt: string;
  connectedByEmail: string | null;
  graphSubscriptionId: string | null;
  graphSubscriptionExpiresAt: string | null;
}

const RETURN_URL = "/integrations/outlook-inbox";

export default function OutlookInbox() {
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mailboxes, setMailboxes] = useState<OutlookMailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  const oauthConnected = searchParams.get("outlookConnected") === "true";
  const oauthError = searchParams.get("outlookError");

  useEffect(() => {
    if (oauthConnected || oauthError) {
      setSearchParams({}, { replace: true });
    }
  }, [oauthConnected, oauthError]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.messaging.get("OutlookOAuth/list");
      const data = await res.json();
      setMailboxes(Array.isArray(data) ? data : []);
    } catch (ex) {
      console.error("OutlookInbox: load failed", ex);
      setMailboxes([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function handleConnect() {
    setConnecting(true);
    setConnectError("");
    try {
      const res = await api.messaging.get(`OutlookOAuth/auth-url?returnUrl=${encodeURIComponent(RETURN_URL)}`);
      const data = await res.json();
      if (!res.ok) {
        setConnectError(data?.error ?? "Failed to start Outlook connection.");
        setConnecting(false);
        return;
      }
      window.location.href = data.authUrl;
    } catch (ex) {
      setConnectError("Failed to start Outlook connection. Please try again.");
      console.error("OutlookInbox: auth-url failed", ex);
      setConnecting(false);
    }
  }

  async function handleToggle(m: OutlookMailbox) {
    setTogglingId(m.id);
    try {
      await api.messaging.patch(`OutlookOAuth/${m.id}/toggle`, {});
      await load();
    } catch (ex) {
      console.error("OutlookInbox: toggle failed", ex);
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDisconnect(m: OutlookMailbox) {
    if (!confirm(`Disconnect ${m.mailboxEmail}? Inbound emails to this mailbox will stop being processed.`)) return;
    setDeletingId(m.id);
    try {
      await api.messaging.delete(`OutlookOAuth/${m.id}`);
      await load();
    } catch (ex) {
      console.error("OutlookInbox: disconnect failed", ex);
    } finally {
      setDeletingId(null);
    }
  }

  if (showWizard) {
    return <StarterWorkflowWizard integration="outlook-inbox" onClose={() => setShowWizard(false)} />;
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0078D4]">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 5.5v13a1.5 1.5 0 0 1-1.5 1.5H1.5A1.5 1.5 0 0 1 0 18.5v-13A1.5 1.5 0 0 1 1.5 4h21A1.5 1.5 0 0 1 24 5.5zm-2 .55L12 13.25 2 6.05V7.5l10 7.25 10-7.25z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Outlook Inbox</h1>
            <p className="text-sm text-slate-500">
              Connect Microsoft 365 mailboxes so every inbound email becomes a ticket or conversation.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SecondaryBlueButton label="Create Workflow" onClick={() => setShowWizard(true)} />
          <PrimaryBlueButton
            label={connecting ? "Connecting..." : "Connect Outlook Account"}
            onClick={handleConnect}
            disabled={connecting}
          />
        </div>
      </div>

      {oauthConnected && (
        <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          Outlook account connected successfully. Inbound emails will now flow into your inbox.
        </div>
      )}
      {oauthError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Outlook connection failed: {oauthError}. Please try again.
        </div>
      )}
      {connectError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {connectError}
        </div>
      )}

      <SetupInstructions />

      <div className="mb-6">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Connected Mailboxes</h2>
        {loading ? (
          <div className="text-sm text-slate-400 py-4">Loading...</div>
        ) : mailboxes.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
            <p className="text-sm font-medium text-slate-600 mb-1">No mailboxes connected yet</p>
            <p className="text-xs text-slate-400 mb-4">
              Connect an Outlook or Microsoft 365 account to start receiving emails as tickets.
            </p>
            <PrimaryBlueButton
              label={connecting ? "Connecting..." : "Connect Your First Mailbox"}
              onClick={handleConnect}
              disabled={connecting}
            />
          </div>
        ) : (
          <div className="space-y-3">
            {mailboxes.map(m => (
              <MailboxRow
                key={m.id}
                mailbox={m}
                toggling={togglingId === m.id}
                deleting={deletingId === m.id}
                onToggle={() => handleToggle(m)}
                onDisconnect={() => handleDisconnect(m)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="OutlookInbox" />
      </div>
    </div>
  );
}

// ── Mailbox row ─────────────────────────────────────────────────────────────

function MailboxRow({
  mailbox: m,
  toggling,
  deleting,
  onToggle,
  onDisconnect,
}: {
  mailbox: OutlookMailbox;
  toggling: boolean;
  deleting: boolean;
  onToggle: () => void;
  onDisconnect: () => void;
}) {
  const subExpired = m.graphSubscriptionExpiresAt
    ? new Date(m.graphSubscriptionExpiresAt) < new Date()
    : false;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex h-2 w-2 rounded-full flex-shrink-0 ${m.isEnabled ? "bg-green-500" : "bg-slate-300"}`} />
            <span className="font-medium text-slate-800 text-sm truncate">{m.mailboxEmail}</span>
            {!m.isEnabled && <span className="text-xs text-slate-400 bg-slate-100 px-1.5 rounded">Disabled</span>}
            {subExpired && (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 rounded">
                Subscription expired
              </span>
            )}
          </div>
          {m.label && <p className="text-xs text-slate-500 mb-0.5">Label: {m.label}</p>}
          {m.teamName && <p className="text-xs text-slate-500 mb-0.5">Team: {m.teamName}</p>}
          {m.connectedByEmail && <p className="text-xs text-slate-400">Connected by {m.connectedByEmail}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button className="text-xs text-slate-500 hover:underline" onClick={onToggle} disabled={toggling}>
            {toggling ? "..." : m.isEnabled ? "Disable" : "Enable"}
          </button>
          <button className="text-xs text-red-500 hover:underline" onClick={onDisconnect} disabled={deleting}>
            {deleting ? "..." : "Disconnect"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Setup instructions ─────────────────────────────────────────────────────────

function SetupInstructions() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-5 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>How to connect Outlook to Velaro</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-slate-100 text-sm text-slate-600 space-y-3">
          <ol className="mt-4 space-y-3">
            {[
              "Click Connect Outlook Account above.",
              "Sign in with the Microsoft 365 account whose inbox you want to connect.",
              "Accept the requested permissions (read and send email).",
              "Velaro creates a Microsoft Graph subscription so new emails arrive in real time.",
              "Assign the mailbox to a team in the mailbox row after connecting.",
              "Repeat for additional mailboxes — each can be assigned to a different team.",
            ].map((text, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-[#0078D4] text-white text-xs font-bold mt-0.5">{i + 1}</span>
                <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
              </li>
            ))}
          </ol>
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800 mt-3">
            <strong>Tip:</strong> Exchange and Outlook.com accounts are both supported. Graph subscriptions
            expire every 3 days — Velaro renews them automatically in the background.
          </div>
        </div>
      )}
    </div>
  );
}
